import { ethers } from "ethers";

import TREE_ABI from "./contract-abi/tree-token-abi.json";
import ERC_ABI from "./contract-abi/erc-abi.json";
import ECOFUTURES_BANK_ABI from "./contract-abi/ecofutures-bank-abi.json";
import ECOFUTURES_TOKEN_ABI from "./contract-abi/ecofutures-token-abi.json";
import ECOFUTURES_ADMIN_ABI from "./contract-abi/ecofutures-admin-abi.json";
import ECOFUTURES_CORE_ABI from "./contract-abi/ecofutures-core-abi.json";
import ECOLENS_ABI from "./contract-abi/ecolens-abi.json";
import OVERCHARGE_ABI from "./contract-abi/ecoovercharge-abi.json";
import GOVERNANCE_ABI from "./contract-abi/ecofutures-governance-abi.json";
import ECO_FORWARDER_ABI from "./contract-abi/ecoforwarder-abi.json";
import ECO_CHALLENGE_ABI from "./contract-abi/ecochallenge-abi.json";
import { getOrCreateClient } from "../contexts/useMetaMask";
import { MainChainID } from "../utils/constants";

// Returns an ethers Signer backed by the @metamask/connect-evm provider.
// Works on desktop (browser extension) and mobile (deep link) — never reads window.ethereum.
// NOTE: getSigner() triggers eth_requestAccounts which pops MetaMask. Only call
// this from user-initiated signing flows. For read-only calls use getReadOnlyContract.
export const getMetaMaskSigner = async () => {
  const client = await getOrCreateClient();
  const provider = client.getProvider();
  const ethersProvider = new ethers.BrowserProvider(provider as any);
  const signer = await ethersProvider.getSigner();
  // Network guard: every signing flow (direct tx or EIP-712 relay) assumes chainId 97 —
  // fail here with a clear message before anything is built on the wrong chain, instead
  // of surfacing an opaque contract/RPC error. The app-wide banner offers the switch.
  const network = await ethersProvider.getNetwork();
  if (Number(network.chainId) !== MainChainID) {
    throw new Error(
      `Wrong network — please switch your wallet to chain ${MainChainID} (BNB Smart Chain Testnet)`
    );
  }
  return signer;
};

// Read-only JSON-RPC provider — no wallet, no popup. Use for view/pure contract
// reads (balances, limits, on-chain metadata) so page loads never prompt MetaMask.
let readOnlyProvider: ethers.JsonRpcProvider | null = null;
export const getReadOnlyProvider = (): ethers.JsonRpcProvider => {
  if (!readOnlyProvider) {
    const rpcUrl = process.env.REACT_APP_RPC_URL || "https://data-seed-prebsc-1-s1.bnbchain.org:8545";
    readOnlyProvider = new ethers.JsonRpcProvider(rpcUrl);
  }
  return readOnlyProvider;
};

// V10.1 — fresh deployment (EcoChallenge panels) || Date: 13 September 2026
// Source of truth: docs/v10/v10.1/addresses.json. Every address is new except FreeToken.

//EcoFutures Core
export const ecoFuturesCoreContractAddress = "0x2FC2cee0946263f732f3217a5f42d49CE39a6521";

//EcoFutures Admin
export const ecoFuturesAdminContractAddress = "0xcC734760A8b80B0FEaF9BE8Bc1938b62a21BaDc1";

//EcoFutures Bank
export const ecoFuturesBankContractAddress = "0x345e0228bD703e67788FF46F786ECADcF2b3ae77";

//Tree
export const treeContractAddress = "0xbe2Ff7dA3C5d7924f8de07608ECFADff04744BE8";

//EcoForwarder
export const ecoForwarderContractAddress = "0x87C6C9d6A41c1e2318832bDA035025182b55e2Ce";

//EcoFutures Token
export const ecoFuturesTokenContractAddress = "0x484716812C83A374595074B863c9DfB26C31f406";

//EcoLens
export const ecoLensContractAddress = "0x5BBb2870bCd526Fd721b6d022a182fD863D56107";

//Overcharge
export const overchargeContractAddress = "0xd14DB44daeDe9E18d25cE775b47440a14190Be0E";

//Governance
export const governanceContractAddress = "0xE09bcAdF0C2A3fEc70403c8442e556c99e0f1217";

// Free Token
export const ercContractAddress = "0x02AB4E9c73e239EAECF8eBE05AeB7c434a80A567";

// EcoChallenge (new in V10 — windows, panels, verdicts, review pool)
export const challengeContractAddress = "0x983E1d3123b145b3E0377A1378d731F5b5663208";

export const getContract = (
  type:
    | "tree-token"
    | "erc20"
    | "ecofutures-bank"
    | "ecofutures-token"
    | "ecofutures-core"
    | "ecofutures-admin"
    | "ecolens"
    | "overcharge"
    | "governance"
    | "challenge"
    | "ecoForwarder",
  signer: any
) => {
  // tree : ABI for the tree contract
  const treeContractABI = TREE_ABI;
  // Free Token : ABI for the ERC20 contract
  const ercContractABI = ERC_ABI;

  // EcoFuturesFund : ABI for the Easement Auction contract
  const ecoFuturesBankContractABI = ECOFUTURES_BANK_ABI;

  // EcoFuturesToken : ABI for the NFT contract
  const ecoFuturesTokenContractABI = ECOFUTURES_TOKEN_ABI;

  //EcoFuturesAdmin ABI
  const ecoFuturesAdminContractABI = ECOFUTURES_ADMIN_ABI;

  //EcoFuturesCore ABI
  const ecoFuturesCoreContractABI = ECOFUTURES_CORE_ABI;

  //EcoLens ABI
  const ecoLensContractABI = ECOLENS_ABI;

  //Overcharge ABI
  const overchargeContractABI = OVERCHARGE_ABI;

  //Governance ABI
  const governanceContractABI = GOVERNANCE_ABI;

  //EcoForwarder ABI
  const ecoForwarderContractABI = ECO_FORWARDER_ABI;

  //EcoChallenge ABI
  const ecoChallengeContractABI = ECO_CHALLENGE_ABI;

  let contract: ethers.Contract | undefined = undefined;
  switch (type) {
    case "erc20": // free token
      contract = new ethers.Contract(ercContractAddress, ercContractABI, signer);
      break;
    case "tree-token": // tree
      contract = new ethers.Contract(treeContractAddress, treeContractABI, signer);
      break;
    case "ecofutures-bank": //ecofutures-bank
      contract = new ethers.Contract(ecoFuturesBankContractAddress, ecoFuturesBankContractABI, signer);
      break;
    case "ecofutures-token": //EcoFuturesToken
      contract = new ethers.Contract(ecoFuturesTokenContractAddress, ecoFuturesTokenContractABI, signer);
      break;

    case "ecofutures-admin": //EcoFuturesAdmin
      contract = new ethers.Contract(ecoFuturesAdminContractAddress, ecoFuturesAdminContractABI, signer);
      break;

    case "ecofutures-core": //EcoFuturesCore
      contract = new ethers.Contract(ecoFuturesCoreContractAddress, ecoFuturesCoreContractABI, signer);
      break;
    case "ecolens": //EcoLens
      contract = new ethers.Contract(ecoLensContractAddress, ecoLensContractABI, signer);
      break;
    case "overcharge": //Overcharge
      contract = new ethers.Contract(overchargeContractAddress, overchargeContractABI, signer);
      break;
    case "governance": //Governance
      contract = new ethers.Contract(governanceContractAddress, governanceContractABI, signer);
      break;
    case "challenge": //Challenge
      contract = new ethers.Contract(challengeContractAddress, ecoChallengeContractABI, signer);
      break;
    case "ecoForwarder": //EcoForwarder
      contract = new ethers.Contract(ecoForwarderContractAddress, ecoForwarderContractABI, signer);
      break;
    default:
      return undefined;
  }

  return contract as ethers.Contract;
};

// Same as getContract but bound to the read-only RPC provider. Use for any
// view/pure read so the page doesn't prompt MetaMask to unlock/connect.
export const getReadOnlyContract = (
  type:
    | "tree-token"
    | "erc20"
    | "ecofutures-bank"
    | "ecofutures-token"
    | "ecofutures-core"
    | "ecofutures-admin"
    | "ecolens"
    | "overcharge"
    | "governance"
    | "ecoForwarder"
    | "challenge"
) => {
  return getContract(type, getReadOnlyProvider());
};
